//@flow
import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { translate, type TFunction } from 'react-i18next';
import { Map } from 'immutable';

import { Loader } from 'common/components/core/loader/Loader';
import { withErrorBoundary } from 'common/components/core/errors';
import PjpGraphsGrid from './PjpGraphsGrid';
import type { PjpInsightsDashboard } from 'common/models/internal/PjpInsightsDashboard';

import {
    personnelClientIdSelector,
    personnelIdSelector,
    isGlobalPersonnelSelector
} from 'common/redux/selectors/sessionSelector';
import { dashboardColors } from 'common/enums/ColorEnum';
import ControlledSetInterval from 'common/utils/ControlledSetInterval';
type LayoutType = {
    i: string,
    x: number,
    y: number,
    w: number,
    h: number,
    minW: number,
    minH: number
};
type OwnProps = {
    areDashboardsLoading: boolean,
    dashboard: ?Object,
    planId: string,
    fetchMetric: (
        metricId: string,
        aggregate: string,
        groupBy: Array<string>,
        graphId: string,
        includeExistingBeat: boolean,
        isEditMetricActive: boolean,
        flushExisting: boolean
    ) => void,
    pollFetchMetric: (clientId: string, poller: ControlledSetInterval) => void,
    clientId: string,
    onEditChart: (dashboardId: string) => void,
    t: TFunction,
    onDeleteChart: (dashboardId: string) => void,
    fetchPjpChartsData: Function,
    allDashboards: Array<PjpInsightsDashboard>,
    graphsWithExistingBeat: Map<string, boolean>,
    toggleExistingBeat: (graphId: string) => void
};

type Props = {
    ...OwnProps,
    personnelClientId: ?string,
    personnelId: ?string,
    isGlobalPersonnel: boolean
};

type State = {
    savedLayout: ?{ [breakpoint: string]: Array<LayoutType> }
};

class PjpMetricGraphs extends React.PureComponent<Props, State> {
    state: State = {
        savedLayout: null
    };

    componentDidMount() {
        this.fetchLayout(this.props);
    }

    componentDidUpdate(nextProps) {
        if (
            this.props.dashboard !== nextProps.dashboard ||
            this.props.personnelId !== nextProps.personnelId ||
            this.props.personnelClientId !== nextProps.personnelClientId
        ) {
            this.fetchLayout(nextProps);
        }
    }

    fetchLayout(props: Props) {
        const { personnelId, personnelClientId, dashboard } = props;
        if (!personnelId || !personnelClientId || !dashboard) {
            return;
        }
        const storedLayoutJson = localStorage.getItem(
            `${personnelClientId}/${personnelId}/pjpAnalytics/${dashboard.dashboardId}`
        );
        if (!storedLayoutJson) {
            this.setState({ savedLayout: null });
            return;
        }
        this.setState({ savedLayout: JSON.parse(storedLayoutJson) });
    }

    handleLayoutChange = (currentLayout, allLayouts) => {
        const { personnelId, personnelClientId, dashboard } = this.props;
        if (dashboard && personnelId && personnelClientId) {
            localStorage.setItem(
                `${personnelClientId}/${personnelId}/pjpAnalytics/${dashboard.dashboardId}`,
                JSON.stringify(allLayouts)
            );
            this.setState({ savedLayout: allLayouts });
        }
    };

    resetLayout = () => {
        const { personnelId, personnelClientId, dashboard } = this.props;
        if (dashboard && personnelId && personnelClientId) {
            localStorage.removeItem(
                `${personnelClientId}/${personnelId}/pjpAnalytics/${dashboard.dashboardId}`
            );
        }
        this.setState({ savedLayout: null });
    };

    render() {
        const {
            dashboard,
            fetchMetric,
            pollFetchMetric,
            areDashboardsLoading,
            clientId,
            onEditChart,
            planId,
            fetchPjpChartsData,
            allDashboards,
            onDeleteChart,
            graphsWithExistingBeat,
            toggleExistingBeat,
            t
        } = this.props;
        const { savedLayout } = this.state;

        if (areDashboardsLoading) {
            return (
                <div>
                    <Loader
                        backgroundColor={dashboardColors.paleGreySeven}
                        active
                    />
                </div>
            );
        }

        if (!dashboard || !dashboard.dashboardData) {
            throw new Error('No dashboard found!');
        }

        return (
            <div>
                <PjpGraphsGrid
                    graphsWithExistingBeat={graphsWithExistingBeat}
                    fetchPjpChartsData={fetchPjpChartsData}
                    t={t}
                    onDeleteChart={onDeleteChart}
                    allDashboards={allDashboards}
                    toggleExistingBeat={toggleExistingBeat}
                    planId={planId}
                    clientId={clientId}
                    pollFetchMetric={pollFetchMetric}
                    fetchMetric={fetchMetric}
                    onEditChart={onEditChart}
                    dashboard={dashboard}
                    savedLayout={savedLayout}
                    onLayoutChange={this.handleLayoutChange}
                />
            </div>
        );
    }
}

export default (compose(
    withErrorBoundary(),
    translate('entityManagagement'),
    connect((state, ownProps) => {
        return {
            personnelClientId: personnelClientIdSelector(state),
            personnelId: personnelIdSelector(state),
            isGlobalPersonnel: isGlobalPersonnelSelector(state, ownProps)
        };
    })
)(PjpMetricGraphs): React.AbstractComponent<OwnProps>);
